/**
 * Careers Page - Olympic Fit Wellness
 * Job listings and company culture
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  MapPin, 
  Clock, 
  Briefcase,
  Heart,
  Users,
  Dumbbell,
  Star,
  ChevronRight
} from 'lucide-react';

export default function CareersPage() {
  const [selectedDepartment, setSelectedDepartment] = useState('all');

  const benefits = [
    { icon: Dumbbell, title: 'Free Membership', description: 'Complimentary Elite membership for you and your family' },
    { icon: Heart, title: 'Health Benefits', description: 'Comprehensive medical, dental, and vision coverage' },
    { icon: Users, title: 'Growth Opportunities', description: 'Career development and continuing education support' },
    { icon: Star, title: 'Performance Bonuses', description: 'Quarterly bonuses based on individual and team goals' }
  ];

  const departments = [
    { id: 'all', name: 'All Departments' },
    { id: 'fitness', name: 'Fitness & Training' },
    { id: 'wellness', name: 'Wellness Services' },
    { id: 'operations', name: 'Operations' },
    { id: 'corporate', name: 'Corporate' }
  ];

  const openPositions = [
    {
      id: 'job_001',
      title: 'Personal Trainer',
      department: 'fitness',
      location: 'Multiple Locations',
      type: 'Full-time',
      description: 'Help members achieve their fitness goals through personalized training programs.'
    },
    {
      id: 'job_002',
      title: 'Group Fitness Instructor',
      department: 'fitness',
      location: 'McKinney, TX',
      type: 'Part-time',
      description: 'Lead engaging group fitness classes in various formats.'
    },
    {
      id: 'job_003',
      title: 'Registered Dietitian',
      department: 'wellness',
      location: 'Frisco, TX',
      type: 'Full-time',
      description: 'Provide nutrition counseling and meal planning services to members.'
    },
    {
      id: 'job_004',
      title: 'Massage Therapist',
      department: 'wellness',
      location: 'Dallas, TX',
      type: 'Full-time',
      description: 'Deliver therapeutic massage and recovery services.'
    },
    {
      id: 'job_005',
      title: 'Front Desk Associate',
      department: 'operations',
      location: 'Plano, TX',
      type: 'Part-time',
      description: 'Provide exceptional member service and facility support.'
    },
    {
      id: 'job_006',
      title: 'General Manager',
      department: 'operations',
      location: 'Allen, TX',
      type: 'Full-time',
      description: 'Lead all operations for a premium fitness location.'
    },
    {
      id: 'job_007',
      title: 'Marketing Manager',
      department: 'corporate',
      location: 'McKinney, TX (HQ)',
      type: 'Full-time',
      description: 'Drive brand awareness and member acquisition initiatives.'
    },
    {
      id: 'job_008',
      title: 'Software Engineer',
      department: 'corporate',
      location: 'Remote',
      type: 'Full-time',
      description: 'Build and maintain our member-facing applications.'
    }
  ];

  const filteredPositions = selectedDepartment === 'all' 
    ? openPositions 
    : openPositions.filter(job => job.department === selectedDepartment);

  return (
    <div className="bg-white" data-testid="careers-page">
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-neutral-900">
        <div className="container mx-auto px-6 lg:px-12 text-center">
          <span className="text-sm font-heading tracking-[0.15em] text-neutral-500 mb-4 block">CAREERS</span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-normal text-white mb-6 tracking-[0.04em] uppercase">
            Join Our Team
          </h1>
          <p className="text-base md:text-lg text-neutral-400 max-w-2xl mx-auto tracking-wide">
            Be part of a team dedicated to transforming lives through comprehensive wellness.
          </p>
        </div>
      </section>

      {/* Why Work With Us */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <span className="text-sm font-heading tracking-[0.15em] text-neutral-500 mb-4 block">WHY OFW</span>
            <h2 className="text-3xl md:text-4xl font-heading font-normal text-neutral-900 mb-6 tracking-[0.04em] uppercase">
              More Than a Job
            </h2>
            <p className="text-neutral-600 max-w-2xl mx-auto tracking-wide">
              At Olympic Fit Wellness, we're building something special. Join a team that's passionate about wellness and committed to excellence.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, idx) => (
              <div key={idx} className="text-center p-8 border border-neutral-200">
                <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center border border-neutral-200">
                  <benefit.icon className="w-8 h-8 text-neutral-900" />
                </div>
                <h3 className="text-lg font-heading font-normal text-neutral-900 mb-3 tracking-wide">
                  {benefit.title}
                </h3>
                <p className="text-neutral-600 text-sm tracking-wide">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Culture */}
      <section className="py-16 lg:py-24 bg-neutral-50">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-sm font-heading tracking-[0.15em] text-neutral-500 mb-4 block">OUR CULTURE</span>
              <h2 className="text-3xl md:text-4xl font-heading font-normal text-neutral-900 mb-6 tracking-[0.04em] uppercase">
                Passion Meets Purpose
              </h2>
              <p className="text-neutral-600 mb-6 tracking-wide leading-relaxed">
                We believe in practicing what we preach. Our team members are passionate about wellness and committed to their own health journeys. We foster an environment of continuous learning, collaboration, and growth.
              </p>
              <ul className="space-y-4">
                {[
                  'Diverse, inclusive workplace',
                  'Continuous learning & development',
                  'Work-life balance prioritized',
                  'Competitive compensation',
                  'Clear career pathways'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-neutral-700">
                    <ChevronRight className="w-5 h-5 text-neutral-900" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="aspect-[4/3] overflow-hidden">
              <img 
                src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800"
                alt="OFW Team Culture"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <span className="text-sm font-heading tracking-[0.15em] text-neutral-500 mb-4 block">OPPORTUNITIES</span>
            <h2 className="text-3xl md:text-4xl font-heading font-normal text-neutral-900 mb-6 tracking-[0.04em] uppercase">
              Open Positions
            </h2>
          </div>

          {/* Department Filter */}
          <div className="flex flex-wrap justify-center gap-2 mb-12">
            {departments.map(dept => (
              <button
                key={dept.id}
                onClick={() => setSelectedDepartment(dept.id)}
                className={`px-6 py-3 text-sm font-heading tracking-[0.06em] transition-all ${
                  selectedDepartment === dept.id
                    ? 'bg-black text-white'
                    : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                }`}
              >
                {dept.name.toUpperCase()}
              </button>
            ))}
          </div>

          {/* Job Listings */}
          <div className="max-w-4xl mx-auto space-y-4">
            {filteredPositions.length > 0 ? (
              filteredPositions.map((job) => (
                <div 
                  key={job.id} 
                  className="border border-neutral-200 p-6 hover:border-neutral-400 transition-colors"
                  data-testid={`job-${job.id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                      <h3 className="text-lg font-heading font-normal text-neutral-900 mb-2 tracking-wide">
                        {job.title}
                      </h3>
                      <p className="text-neutral-600 text-sm mb-3">{job.description}</p>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-neutral-500">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {job.location}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {job.type}
                        </span>
                        <span className="flex items-center gap-1">
                          <Briefcase className="w-4 h-4" />
                          {departments.find(d => d.id === job.department)?.name}
                        </span>
                      </div>
                    </div>
                    <Link
                      to={`/careers/${job.id}`}
                      className="inline-flex items-center gap-2 bg-black text-white font-heading px-6 py-3 text-sm tracking-[0.06em] hover:bg-neutral-800 transition-colors whitespace-nowrap"
                    >
                      APPLY <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12 text-neutral-500">
                No positions available in this department at this time.
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-24 bg-neutral-900">
        <div className="container mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-normal text-white mb-6 tracking-[0.04em] uppercase">
            Don't See the Right Fit?
          </h2>
          <p className="text-neutral-400 max-w-2xl mx-auto mb-10 tracking-wide">
            We're always looking for talented individuals. Send us your resume and we'll reach out when a matching opportunity opens up.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-white text-black font-heading px-10 py-4 text-sm tracking-[0.08em] hover:bg-neutral-100 transition-colors"
          >
            SEND YOUR RESUME <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
